// Service Supabase Direct - Remplace les appels backend /api/*
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://xjhktmconhidqpatxflw.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhqaGt0bWNvbmhpZHFwYXR4Zmx3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQwMTc5OTgsImV4cCI6MjA4OTU5Mzk5OH0.yCv9MR-oEke-Y25k9UONEMRKQLe9lSMUY2tjOaqWfl8';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ============ UTILS ============

// Génère un UUID Supabase à partir d'un Firebase UID
export const generateUUIDFromFirebaseUID = (firebaseUID) => {
  if (!firebaseUID) return null;
  let hash = 0;
  for (let i = 0; i < firebaseUID.length; i++) {
    const char = firebaseUID.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const hex = Math.abs(hash).toString(16).padStart(8, '0');
  const uuid = `${hex.slice(0, 8)}-${hex.slice(0, 4)}-4${hex.slice(1, 4)}-a${hex.slice(0, 3)}-${hex.padEnd(12, '0').slice(0, 12)}`;
  return uuid;
};

// ============ VIDEOS ============

// GET /api/videos/ - Récupérer toutes les vidéos publiées
export const getVideos = async ({ limit = 20, skip = 0, content_type_id = null } = {}) => {
  try {
    let query = supabase
      .from('videos')
      .select('*, users(username, avatar_url)')
      .eq('status', 'published')
      .eq('is_published', true)
      .order('created_at', { ascending: false })
      .range(skip, skip + limit - 1);
    
    if (content_type_id) {
      query = query.eq('content_type_id', content_type_id);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    return {
      success: true,
      data: data || [],
      pagination: { limit, skip, count: data?.length || 0 }
    };
  } catch (error) {
    console.error('Erreur getVideos:', error);
    return { success: false, data: [], error: error.message };
  }
};

// GET /api/videos/:id - Récupérer une vidéo par ID
export const getVideoById = async (videoId) => {
  try {
    const { data, error } = await supabase
      .from('videos')
      .select('*, users(username, avatar_url), ai_models(name), languages(name), content_types(name)')
      .eq('id', videoId)
      .single();

    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Erreur getVideoById:', error);
    return { success: false, data: null, error: error.message };
  }
};

// GET /api/videos/user/:userId - Vidéos d'un utilisateur
export const getUserVideos = async (firebaseUserId, { limit = 50, skip = 0 } = {}) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('user_id', supabaseUUID)
      .order('created_at', { ascending: false })
      .range(skip, skip + limit - 1);

    if (error) throw error;
    
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Erreur getUserVideos:', error);
    return { success: false, data: [], error: error.message };
  }
};

// GET /api/videos/user/:userId/stats - Stats d'un créateur
export const getUserVideoStats = async (firebaseUserId) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const { data, error } = await supabase
      .from('videos')
      .select('id, status')
      .eq('user_id', supabaseUUID);

    if (error) throw error;
    
    const total = data?.length || 0;
    const published = data?.filter(v => v.status === 'published').length || 0;
    const processing = data?.filter(v => v.status === 'processing').length || 0;
    
    return {
      success: true,
      data: {
        total_videos: total,
        published_videos: published,
        processing_videos: processing,
        total_views: 0, // TODO: calculer depuis table views
        total_likes: 0  // TODO: calculer depuis table likes
      }
    };
  } catch (error) {
    console.error('Erreur getUserVideoStats:', error);
    return { success: false, data: null, error: error.message };
  }
};

// DELETE /api/videos/:id - Supprimer une vidéo
export const deleteVideo = async (videoId, firebaseUserId) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    // Vérifier que l'utilisateur est propriétaire
    const { data: video, error: fetchError } = await supabase
      .from('videos')
      .select('id, user_id')
      .eq('id', videoId)
      .single();
    
    if (fetchError || !video) {
      return { success: false, error: 'Vidéo non trouvée' };
    }
    
    if (video.user_id !== supabaseUUID) {
      return { success: false, error: 'Accès non autorisé' };
    }
    
    // Supprimer la vidéo
    const { error } = await supabase
      .from('videos')
      .delete()
      .eq('id', videoId);

    if (error) throw error;
    
    return { success: true, message: 'Vidéo supprimée' };
  } catch (error) {
    console.error('Erreur deleteVideo:', error);
    return { success: false, error: error.message };
  }
};

// ============ USERS / PROFILE ============

// GET /api/users/:userId/profile
export const getUserProfile = async (firebaseUserId) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', supabaseUUID)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Erreur getUserProfile:', error);
    return { success: false, data: null, error: error.message };
  }
};

// PUT /api/users/:userId/profile
export const updateUserProfile = async (firebaseUserId, profileData) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const updateData = {};
    if (profileData.username) updateData.username = profileData.username;
    if (profileData.biographie !== undefined) updateData.biographie = profileData.biographie;
    if (profileData.phone_number !== undefined) updateData.phone_number = profileData.phone_number;
    if (profileData.avatar_url) updateData.avatar_url = profileData.avatar_url;
    
    const { data, error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', supabaseUUID)
      .select()
      .single();

    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Erreur updateUserProfile:', error);
    return { success: false, error: error.message };
  }
};

// PUT /api/users/:userId/role
export const updateUserRole = async (firebaseUserId, newRole) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const { data, error } = await supabase
      .from('users')
      .update({ role: newRole })
      .eq('id', supabaseUUID)
      .select()
      .single();

    if (error) throw error;
    
    return { success: true, data, message: `Rôle mis à jour vers '${newRole}'` };
  } catch (error) {
    console.error('Erreur updateUserRole:', error);
    return { success: false, error: error.message };
  }
};

// POST /api/users/:userId/profile - Créer un profil
export const createUserProfile = async (firebaseUserId, { email, username, role = 'viewer' }) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    // Vérifier si existe déjà
    const { data: existing } = await supabase
      .from('users')
      .select('id')
      .eq('id', supabaseUUID)
      .single();
    
    if (existing) {
      return { success: true, data: existing, message: 'Profil existe déjà' };
    }
    
    const { data, error } = await supabase
      .from('users')
      .insert([{
        id: supabaseUUID,
        email,
        username,
        role,
        created_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Erreur createUserProfile:', error);
    return { success: false, error: error.message };
  }
};

// DELETE /api/users/:userId/profile
export const deleteUserProfile = async (firebaseUserId) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    // Supprimer dans l'ordre (contraintes FK)
    await supabase.from('watchlist').delete().eq('user_id', supabaseUUID);
    await supabase.from('videos').delete().eq('user_id', supabaseUUID);
    
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', supabaseUUID);

    if (error) throw error;
    
    return { success: true, message: 'Compte supprimé' };
  } catch (error) {
    console.error('Erreur deleteUserProfile:', error);
    return { success: false, error: error.message };
  }
};

// ============ WATCHLIST ============

// GET /api/watchlist/user/:userId
export const getUserWatchlist = async (firebaseUserId) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const { data, error } = await supabase
      .from('watchlist')
      .select('*, videos(*)')
      .eq('user_id', supabaseUUID)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return {
      success: true,
      data: data || [],
      stats: { total: data?.length || 0 }
    };
  } catch (error) {
    console.error('Erreur getUserWatchlist:', error);
    return { success: false, data: [], error: error.message };
  }
};

// POST /api/watchlist/add
export const addToWatchlist = async (firebaseUserId, videoId) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const { data, error } = await supabase
      .from('watchlist')
      .insert([{
        user_id: supabaseUUID,
        video_id: videoId,
        created_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Erreur addToWatchlist:', error);
    return { success: false, error: error.message };
  }
};

// DELETE /api/watchlist/video/:videoId
export const removeFromWatchlist = async (firebaseUserId, videoId) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const { error } = await supabase
      .from('watchlist')
      .delete()
      .eq('user_id', supabaseUUID)
      .eq('video_id', videoId);

    if (error) throw error;
    
    return { success: true, message: 'Retiré de la watchlist' };
  } catch (error) {
    console.error('Erreur removeFromWatchlist:', error);
    return { success: false, error: error.message };
  }
};

// GET /api/watchlist/check/:videoId
export const checkInWatchlist = async (firebaseUserId, videoId) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const { data, error } = await supabase
      .from('watchlist')
      .select('id')
      .eq('user_id', supabaseUUID)
      .eq('video_id', videoId)
      .single();

    return { success: true, in_watchlist: !!data };
  } catch (error) {
    return { success: true, in_watchlist: false };
  }
};

// GET /api/watchlist/count/:userId
export const getWatchlistCount = async (firebaseUserId) => {
  try {
    const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
    
    const { count, error } = await supabase
      .from('watchlist')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', supabaseUUID);

    if (error) throw error;
    
    return { success: true, count: count || 0 };
  } catch (error) {
    console.error('Erreur getWatchlistCount:', error);
    return { success: false, count: 0, error: error.message };
  }
};

// ============ METADATA ============

// GET /api/metadata/content-types
// GET /api/metadata/content-types
export const getContentTypes = async () => {
  try {
    const { data, error } = await supabase
      .from('content_types')
      .select('*')
      .order('label');

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Erreur getContentTypes:', error);
    return { success: false, data: [], error: error.message };
  }
};

// GET /api/metadata/ai-models
export const getAIModels = async () => {
  try {
    const { data, error } = await supabase
      .from('ai_models')
      .select('*')
      .order('name');

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Erreur getAIModels:', error);
    return { success: false, data: [], error: error.message };
  }
};

// GET /api/metadata/genres
export const getGenres = async () => {
  try {
    const { data, error } = await supabase
      .from('genres')
      .select('*')
      .order('name');

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Erreur getGenres:', error);
    return { success: false, data: [], error: error.message };
  }
};

// GET /api/metadata/languages
export const getLanguages = async () => {
  try {
    const { data, error } = await supabase
      .from('languages')
      .select('*')
      .order('name');

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Erreur getLanguages:', error);
    return { success: false, data: [], error: error.message };
  }
};

// GET /api/metadata/series
export const getSeries = async (firebaseUserId = null) => {
  try {
    let query = supabase
      .from('series')
      .select('*')
      .order('title');
    
    if (firebaseUserId) {
      const supabaseUUID = generateUUIDFromFirebaseUID(firebaseUserId);
      query = query.eq('user_id', supabaseUUID);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Erreur getSeries:', error);
    return { success: false, data: [], error: error.message };
  }
};

// GET /api/metadata/series/:seriesId/seasons
export const getSeasons = async (seriesId) => {
  try {
    const { data, error } = await supabase
      .from('seasons')
      .select('*')
      .eq('series_id', seriesId)
      .order('season_number');

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Erreur getSeasons:', error);
    return { success: false, data: [], error: error.message };
  }
};

// GET /api/categories/
export const getCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('name');

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Erreur getCategories:', error);
    return { success: false, data: [], error: error.message };
  }
};

// ============ HEALTH CHECK ============

export const healthCheck = async () => {
  try {
    const { data, error } = await supabase.from('users').select('id').limit(1);
    return {
      status: 'OK',
      services: {
        supabase: { connection: error ? 'ERROR' : 'OK' }
      },
      architecture: 'Frontend Direct + Supabase + Cloudflare Stream'
    };
  } catch (error) {
    return {
      status: 'ERROR',
      services: { supabase: { connection: 'ERROR' } },
      error: error.message
    };
  }
};

export default {
  // Videos
  getVideos,
  getVideoById,
  getUserVideos,
  getUserVideoStats,
  deleteVideo,
  // Users
  getUserProfile,
  updateUserProfile,
  updateUserRole,
  createUserProfile,
  deleteUserProfile,
  // Watchlist
  getUserWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  checkInWatchlist,
  getWatchlistCount,
  // Metadata
  getContentTypes,
  getAIModels,
  getGenres,
  getLanguages,
  getSeries,
  getSeasons,
  getCategories,
  // Utils
  generateUUIDFromFirebaseUID,
  healthCheck
};
